<template>
    <app-layout>
        <page-header :title="trans('subscription.notifications.page_title')" title-in-app-header>
            <teleport defer to="#page-header-action">
                <div class="flex w-full items-center justify-between gap-4">
                    <ul class="tabs-list list-unstyled mb-0 inline-flex" role="tablist">
                        <li v-for="(tab, index) in tabs" :key="tab.route" :class="{ active: tab.route === activeTab }" class="tab-item">
                            <a
                                :id="`tab-${tab.route}`"
                                :ref="(el) => (tabEls[index] = el)"
                                :aria-controls="`panel-${tab.route}`"
                                :aria-selected="tab.route === activeTab"
                                :tabindex="tab.route === activeTab ? 0 : -1"
                                class="cursor-pointer"
                                role="tab"
                                @click="activeTab = tab.route"
                                @keydown="onTabKeydown($event, index)"
                                >{{ tab.label }}</a
                            >
                        </li>
                    </ul>
                    <template v-if="$page.props.can_edit && activeTab === 'defaults'">
                        <jet-button v-if="!editMode" type="button" variant="secondary" @click="enterEditMode">{{
                            trans('subscription.notifications.button_edit')
                        }}</jet-button>
                        <div v-else class="flex items-center gap-2">
                            <jet-button type="button" variant="tertiary" @click="cancelEdit">{{
                                trans('subscription.notifications.button_cancel')
                            }}</jet-button>
                            <jet-button :disabled="changeCount === 0" type="button" variant="secondary" @click="openConfirm">{{
                                trans('subscription.notifications.button_save', { count: changeCount })
                            }}</jet-button>
                        </div>
                    </template>
                </div>
            </teleport>
        </page-header>

        <div v-show="activeTab === 'defaults'" id="panel-defaults" role="tabpanel" aria-labelledby="tab-defaults">
            <div class="mb-6 rounded-lg border bg-white p-4 text-sm shadow-sm">
                {{ trans('subscription.notifications.description') }}
            </div>

            <div class="mb-4 flex flex-wrap gap-2">
                <button
                    v-for="channel in channels"
                    :key="channel.value"
                    :class="channel.value === activeChannel ? 'bg-primary text-white' : 'bg-white text-gray-700 hover:bg-gray-50'"
                    class="cursor-pointer rounded-full border px-4 py-1.5 text-sm font-medium"
                    type="button"
                    @click="activeChannel = channel.value"
                >
                    {{ channel.label }}
                    <span v-if="channelChangeCount(channel.value) > 0" class="ms-1 rounded-full bg-amber-500 px-1.5 text-xs text-white">
                        {{ channelChangeCount(channel.value) }}
                    </span>
                </button>
            </div>

            <div class="mb-8 overflow-x-auto rounded-lg border bg-white shadow-sm">
                <table class="w-full border-collapse text-sm">
                    <thead>
                        <tr class="border-b bg-gray-50">
                            <th class="p-3 text-start font-semibold">{{ trans('subscription.notifications.table_notification') }}</th>
                            <th v-for="subscriber in subscribers" :key="subscriber.key" class="p-3 text-center font-semibold">
                                <div>{{ subscriber.label }}</div>
                                <div class="text-xs font-normal text-gray-400">
                                    {{
                                        subscriber.kind === 'role'
                                            ? trans('subscription.notifications.kind_role')
                                            : trans('subscription.notifications.kind_stakeholder')
                                    }}
                                </div>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="notification in notifications" :key="notification.type" class="border-b last:border-b-0">
                            <td class="p-3 align-middle">
                                <div class="font-medium">{{ notification.label }}</div>
                                <div class="text-xs text-gray-400">
                                    {{
                                        notification.scope === 'global'
                                            ? trans('subscription.notifications.scope_global')
                                            : trans('subscription.notifications.scope_per_asset')
                                    }}
                                </div>
                            </td>
                            <td v-for="subscriber in subscribers" :key="subscriber.key" class="p-3 text-center align-middle">
                                <template v-if="isChannelSupported(notification)">
                                    <button
                                        v-if="editMode"
                                        :aria-label="`${notification.label} · ${subscriber.label} · ${activeChannelLabel}`"
                                        :aria-pressed="isEnabled(notification, subscriber)"
                                        class="inline-flex h-7 w-7 cursor-pointer items-center justify-center rounded-full border-0 bg-transparent p-0"
                                        type="button"
                                        @click="toggle(notification, subscriber)"
                                    >
                                        <cell-dot
                                            aria-hidden="true"
                                            :changed="isChanged(notification, subscriber)"
                                            :enabled="isEnabled(notification, subscriber)"
                                        />
                                    </button>
                                    <template v-else>
                                        <cell-dot aria-hidden="true" :changed="false" :enabled="isEnabled(notification, subscriber)" />
                                        <span class="sr-only">{{
                                            isEnabled(notification, subscriber)
                                                ? trans('subscription.notifications.state_on')
                                                : trans('subscription.notifications.state_off')
                                        }}</span>
                                    </template>
                                </template>
                                <span
                                    v-else
                                    :aria-label="trans('subscription.notifications.channel_unavailable', { channel: activeChannelLabel })"
                                    :title="trans('subscription.notifications.channel_unavailable', { channel: activeChannelLabel })"
                                    class="inline-flex h-7 w-7 items-center justify-center text-gray-300"
                                    >–</span
                                >
                            </td>
                        </tr>

                        <template v-if="staticNotifications.length">
                            <tr class="border-t-2 border-b bg-gray-100">
                                <td :colspan="subscribers.length + 1" class="px-3 py-2 text-xs font-semibold tracking-wide text-gray-500 uppercase">
                                    {{ trans('subscription.notifications.static_section') }}
                                </td>
                            </tr>
                            <tr v-for="notification in staticNotifications" :key="notification.type" class="border-b bg-gray-50/60 last:border-b-0">
                                <td class="p-3 align-middle">
                                    <div class="flex items-center gap-2 font-medium text-gray-600">
                                        {{ notification.label }}
                                        <span class="rounded-full bg-gray-200 px-2 py-0.5 text-xs font-normal text-gray-600">{{
                                            trans('subscription.notifications.badge_immutable')
                                        }}</span>
                                    </div>
                                </td>
                                <td :colspan="subscribers.length" class="p-3 align-middle text-sm text-gray-500">
                                    {{ notification.audience }}
                                </td>
                            </tr>
                        </template>
                    </tbody>
                </table>
            </div>

            <confirm-changes-modal ref="confirmModalRef" :changes="changeList" :processing="submitting" @confirm="submit" />
        </div>

        <div v-show="activeTab === 'pm'" id="panel-pm" role="tabpanel" aria-labelledby="tab-pm">
            <Deferred data="pm_notifications">
                <template #fallback>
                    <div class="rounded-lg border bg-white p-6 text-sm text-gray-500 shadow-sm">
                        {{ trans('subscription.notifications.loading_pm') }}
                    </div>
                </template>
                <pm-notifications-panel :configurations="pmNotifications" />
            </Deferred>
        </div>
    </app-layout>
</template>

<script setup>
import { computed, ref } from 'vue';
import { Deferred, router, usePage } from '@inertiajs/vue3';
import { visitModal } from '@inertiaui/modal-vue';
import { trans } from '@/mixins/i18n';
import AppLayout from '@/Layouts/AppLayout.vue';
import JetButton from '@/Jetstream/Button.vue';
import PageHeader from '@/Components/PageHeader.vue';
import CellDot from '@/Pages/Subscriptions/NotificationSettings/components/CellDot.vue';
import ConfirmChangesModal from '@/Pages/Subscriptions/NotificationSettings/components/ConfirmChangesModal.vue';
import PmNotificationsPanel from '@/Pages/Subscriptions/NotificationSettings/components/PmNotificationsPanel.vue';

const page = usePage();

const tabs = [
    { route: 'defaults', label: trans('subscription.notifications.tab_defaults') },
    { route: 'pm', label: trans('subscription.notifications.tab_pm') },
];
const activeTab = ref('defaults');
const tabEls = ref([]);

function onTabKeydown(event, index) {
    if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        activeTab.value = tabs[index].route;
        return;
    }

    const moves = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
    let target = null;
    if (event.key in moves) {
        target = (index + moves[event.key] + tabs.length) % tabs.length;
    } else if (event.key === 'Home') {
        target = 0;
    } else if (event.key === 'End') {
        target = tabs.length - 1;
    } else {
        return;
    }

    event.preventDefault();
    activeTab.value = tabs[target].route;
    tabEls.value[target]?.focus();
}

const notifications = computed(() => page.props.notifications);
const staticNotifications = computed(() => page.props.static_notifications ?? []);
const pmNotifications = computed(() => page.props.pm_notifications ?? []);
const subscribers = computed(() => page.props.subscribers);
const channels = computed(() => page.props.channels);

const editMode = ref(false);
const pending = ref({});
const submitting = ref(false);
const confirmModalRef = ref(null);
const activeChannel = ref(channels.value[0]?.value ?? null);

const activeChannelLabel = computed(() => channels.value.find((c) => c.value === activeChannel.value)?.label ?? activeChannel.value);

const changeList = computed(() => Object.values(pending.value).filter((change) => change.enabled !== change.original));

const changeCount = computed(() => changeList.value.length);

function keyFor(type, subscriberKey, channel) {
    return `${type}::${subscriberKey}::${channel}`;
}

function originalEnabled(notification, subscriberKey, channel) {
    return (notification.subscriptions[subscriberKey] ?? []).includes(channel);
}

function isChannelSupported(notification) {
    return (notification.supported_channels ?? []).includes(activeChannel.value);
}

function isEnabled(notification, subscriber) {
    const key = keyFor(notification.type, subscriber.key, activeChannel.value);
    return pending.value[key]?.enabled ?? originalEnabled(notification, subscriber.key, activeChannel.value);
}

function isChanged(notification, subscriber) {
    const change = pending.value[keyFor(notification.type, subscriber.key, activeChannel.value)];
    return change ? change.enabled !== change.original : false;
}

function channelChangeCount(channel) {
    return changeList.value.filter((change) => change.channel === channel).length;
}

function toggle(notification, subscriber) {
    if (!isChannelSupported(notification)) {
        return;
    }

    const channel = activeChannel.value;
    const key = keyFor(notification.type, subscriber.key, channel);
    const original = originalEnabled(notification, subscriber.key, channel);
    const next = !isEnabled(notification, subscriber);

    pending.value = {
        ...pending.value,
        [key]: {
            notification_type: notification.type,
            notification_label: notification.label,
            subscriber_kind: subscriber.kind,
            subscriber_key: subscriber.key,
            subscriber_label: subscriber.label,
            channel,
            channel_label: channels.value.find((c) => c.value === channel)?.label ?? channel,
            enabled: next,
            original,
        },
    };
}

function enterEditMode() {
    editMode.value = true;
    pending.value = {};
}

function cancelEdit() {
    editMode.value = false;
    pending.value = {};
}

function openConfirm() {
    visitModal('#confirm-notification-changes');
}

function submit() {
    const changes = changeList.value.map((change) => ({
        notification_type: change.notification_type,
        subscriber_kind: change.subscriber_kind,
        subscriber_key: change.subscriber_key,
        channel: change.channel,
        enabled: change.enabled,
    }));

    submitting.value = true;
    router.put(
        route('notification-settings.update'),
        { changes },
        {
            preserveScroll: true,
            onSuccess: () => {
                editMode.value = false;
                pending.value = {};
                confirmModalRef.value?.close();
            },
            onFinish: () => {
                submitting.value = false;
            },
        },
    );
}
</script>
